/**
 * Player API Request Schemas
 *
 * Input parameter schemas for Player API endpoints.
 * All schemas validate request data before sending to the API.
 */

import { z } from 'zod';
import { PLAYER_API_CONFIG } from '@/constants/player/config';

const { LIMITS } = PLAYER_API_CONFIG;

// ----- Passenger Configuration -----

export const ChildSchema = z.object({
  age: z
    .number()
    .int()
    .min(LIMITS.MIN_CHILD_AGE, `Child age must be at least ${LIMITS.MIN_CHILD_AGE}`)
    .max(LIMITS.MAX_CHILD_AGE, `Child age cannot exceed ${LIMITS.MAX_CHILD_AGE}`),
});

export const PassengerConfigSchema = z.object({
  adults: z
    .number()
    .int()
    .min(1, 'At least 1 adult is required')
    .max(LIMITS.MAX_ADULTS, `Maximum ${LIMITS.MAX_ADULTS} adults allowed`),
  children: z
    .array(ChildSchema)
    .max(LIMITS.MAX_CHILDREN, `Maximum ${LIMITS.MAX_CHILDREN} children allowed`),
  passengers: z
    .array(
      z.object({
        age: z.number().int().min(0),
        type: z.enum(['adult', 'child']),
      })
    )
    .optional(),
});

// ----- Period Configuration -----

export const PeriodConfigSchema = z.object({
  start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  duration: z
    .number()
    .int()
    .min(1, 'Duration must be at least 1 day')
    .max(LIMITS.MAX_DURATION, `Maximum duration is ${LIMITS.MAX_DURATION} days`),
  durations: z.array(z.number().int().min(1).max(LIMITS.MAX_DURATION)).optional(),
  preferredDurations: z.array(z.number().int().min(1).max(LIMITS.MAX_DURATION)).optional(),
});

// ----- API Endpoint Parameters -----

export const GetProductListParamsSchema = PassengerConfigSchema.extend({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  duration: z.number().int().min(1).max(LIMITS.MAX_DURATION),
  durations: z.array(z.number().int().min(1).max(LIMITS.MAX_DURATION)).optional(),
  preferredDurations: z.array(z.number().int().min(1).max(LIMITS.MAX_DURATION)).optional(),
  countryCode: z.string().length(2).optional(),
  sortBy: z
    .enum([
      'Price',
      'TotalPrice',
      'Recommendation',
      'Rating',
      'Category',
      'Hotelname',
      'DepartureDate',
      'Duration',
      'Priority',
    ])
    .optional(),
  // Pagination parameters (API uses strings, not numbers)
  resultsFrom: z.string().optional(),
  resultsTotal: z.string().optional(),
  // Accommodation property filters (e.g., "UnitFacilities.SeaView", "BoardType.AllInclusive")
  accommodationProperties: z.array(z.string()).optional(),
  requestBooking: z.enum(['Exclude']).optional(),
});

export const GetProductOffersParamsSchema = PassengerConfigSchema.extend({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format (expected YYYY-MM-DD)'),
  duration: z.number().int().min(1).max(LIMITS.MAX_DURATION),
  productCode: z.string().min(1, 'Product code is required'),
  resultsTotal: z.number().int().positive().optional(),
});

// ----- Type Exports -----

export type Child = z.infer<typeof ChildSchema>;
export type PassengerConfig = z.infer<typeof PassengerConfigSchema>;
export type PeriodConfig = z.infer<typeof PeriodConfigSchema>;
export type GetProductListParams = z.infer<typeof GetProductListParamsSchema>;
export type GetProductOffersParams = z.infer<typeof GetProductOffersParamsSchema>;
